"use client";

import { useState } from "react";
import { Loader } from "lucide-react";
import { useLocale } from "next-intl";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAppContext } from "@/contexts/app";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const isAuthError = (message?: string | null) => {
  if (!message) {
    return false;
  }

  const normalized = message.toLowerCase();
  return (
    normalized.includes("no auth") ||
    normalized.includes("unauth") ||
    normalized.includes("sign-in") ||
    normalized.includes("sign in")
  );
};

type PersonaBuyButtonProps = {
  productId: string;
  itemName: string;
  price: number;
  buttonText: string;
  uiSource: string;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
};

export default function PersonaBuyButton({
  productId,
  itemName,
  price,
  buttonText,
  uiSource,
  className,
  size = "default",
}: PersonaBuyButtonProps) {
  const locale = useLocale();
  const { setShowSignModal } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    try {
      trackEvent("begin_checkout", {
        plan_id: productId,
        interval: "one-time",
        currency: "USD",
        value: price,
        items: [
          {
            item_id: productId,
            item_name: itemName,
            price,
            currency: "USD",
            quantity: 1,
          },
        ],
        ui_source: uiSource,
      });

      setIsLoading(true);

      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product_id: productId,
          currency: "usd",
          locale: locale || "en",
        }),
      });

      const payload = await response.json().catch(() => null);
      if (response.status === 401) {
        setShowSignModal(true);
        return;
      }

      if (!payload) {
        toast.error("checkout failed");
        return;
      }

      if (payload.code !== 0) {
        if (isAuthError(payload.message)) {
          setShowSignModal(true);
          return;
        }

        toast.error(payload.message || "checkout failed");
        return;
      }

      const checkoutUrl = payload.data?.checkout_url;
      if (!checkoutUrl) {
        toast.error("checkout failed");
        return;
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.log("persona checkout failed", error);
      toast.error("checkout failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      size={size}
      className={cn(className)}
      disabled={isLoading}
      onClick={handleCheckout}
    >
      {isLoading ? <Loader className="size-4 animate-spin" /> : null}
      {buttonText}
    </Button>
  );
}
